import { createContext, createElement, useContext, useRef, type ReactNode } from 'react';
import { createStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

const NOOP = () => {};

/** Match `preview-zoom.store` so image and PDF zoom feel consistent. */
export const PDF_MIN_ZOOM = 0.25;
export const PDF_MAX_ZOOM = 4;
const PDF_ZOOM_STEP = 1.25;

function clampZoom(z: number): number {
  if (z < PDF_MIN_ZOOM) return PDF_MIN_ZOOM;
  if (z > PDF_MAX_ZOOM) return PDF_MAX_ZOOM;
  return z;
}

interface PdfViewerStore {
  /** 1-indexed page currently visible at the top of the scroll container. */
  currentPage: number;
  totalPages: number;
  /** Multiplier applied on top of the fit-to-width scale. 1 = fit-to-width. */
  zoomLevel: number;
  /**
   * Imperative scroll-to-page handle the active PdfViewer registers on mount
   * and clears on unmount. Consumers (e.g. the page navigator rendered next
   * to the toolbar) call this to scroll the viewer.
   */
  goToPage: (page: number) => void;
  setCurrentPage: (page: number) => void;
  setTotalPages: (total: number) => void;
  setGoToPage: (fn: (page: number) => void) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  reset: () => void;
}

function createPdfViewerStore() {
  return createStore<PdfViewerStore>((set) => ({
    currentPage: 1,
    totalPages: 0,
    zoomLevel: 1,
    goToPage: NOOP,
    setCurrentPage: (page) => set((s) => (s.currentPage === page ? s : { currentPage: page })),
    setTotalPages: (total) => set((s) => (s.totalPages === total ? s : { totalPages: total })),
    setGoToPage: (fn) => set({ goToPage: fn }),
    zoomIn: () => set((s) => ({ zoomLevel: clampZoom(s.zoomLevel * PDF_ZOOM_STEP) })),
    zoomOut: () => set((s) => ({ zoomLevel: clampZoom(s.zoomLevel / PDF_ZOOM_STEP) })),
    resetZoom: () => set({ zoomLevel: 1 }),
    reset: () => set({ currentPage: 1, totalPages: 0, zoomLevel: 1, goToPage: NOOP }),
  }));
}

type PdfViewerStoreApi = ReturnType<typeof createPdfViewerStore>;

const PdfViewerContext = createContext<PdfViewerStoreApi | null>(null);

export function PdfViewerProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<PdfViewerStoreApi | null>(null);
  if (!storeRef.current) storeRef.current = createPdfViewerStore();
  return createElement(PdfViewerContext.Provider, { value: storeRef.current }, children);
}

function usePdfViewerStoreApi(): PdfViewerStoreApi {
  const ctx = useContext(PdfViewerContext);
  if (!ctx) throw new Error('PDF viewer hooks must be used inside <PdfViewerProvider>');
  return ctx;
}

export function usePdfViewerStore<T>(
  selector: (state: PdfViewerStore) => T,
  equalityFn: (a: T, b: T) => boolean = Object.is,
): T {
  const store = usePdfViewerStoreApi();
  return useStoreWithEqualityFn(store, selector, equalityFn);
}
